import type { AdAttribution } from '@order-up/models';

export type { AdAttribution };

/** Largest encoded fragment value accepted (bytes of the base64url text). */
export const AD_ATTRIBUTION_MAX_ENCODED_LENGTH = 2048;
/** Longest value any single string field may carry. */
export const AD_ATTRIBUTION_MAX_FIELD_LENGTH = 256;
/** URL-fragment key (alongside `handoff`) that carries the encoded attribution. */
export const AD_ATTRIBUTION_FRAGMENT_KEY = 'attr';

// No `gclid`: nothing sends it (GA4 MP and Meta CAPI take neither), so it is not collected.
const STRING_FIELDS = ['gaClientId', 'gaSessionId', 'fbp', 'fbc', 'landingUrl'] as const;
const ALLOWED_KEYS = new Set<string>([...STRING_FIELDS, 'marketingAllowed']);

function readCookie(doc: Pick<Document, 'cookie'>, name: string): string | undefined {
    const raw = doc.cookie || '';
    for (const part of raw.split(';')) {
        const eq = part.indexOf('=');
        if (eq < 0) continue;
        if (part.slice(0, eq).trim() !== name) continue;
        const value = part.slice(eq + 1).trim();
        try {
            return decodeURIComponent(value);
        } catch {
            return value;
        }
    }
    return undefined;
}

/** `GA1.1.<a>.<b>` -> `<a>.<b>`. */
function parseGaClientId(cookie: string | undefined): string | undefined {
    if (!cookie) return undefined;
    const parts = cookie.split('.');
    if (parts.length < 4) return undefined;
    const id = `${parts[2]}.${parts[3]}`;
    return /^\d+\.\d+$/.test(id) ? id : undefined;
}

/**
 * GA4 session cookie: `GS1.1.<sid>.<n>.…` (older) or `GS2.1.s<sid>$o1$g1…`
 * (current). Both carry the session id right after the `GSx.y.` prefix.
 */
function parseGaSessionId(cookie: string | undefined): string | undefined {
    if (!cookie) return undefined;
    const m = /^GS\d\.\d+\.s?(\d+)/.exec(cookie);
    return m ? m[1] : undefined;
}

function clean(value: string | undefined): string | undefined {
    if (!value) return undefined;
    const v = value.trim();
    return v.length > 0 && v.length <= AD_ATTRIBUTION_MAX_FIELD_LENGTH ? v : undefined;
}

/**
 * Read the ad identifiers the browser holds on the origin page.
 *
 * `decision` is `useMerchantMarketingTagsDecision()?.load`. When it is false
 * (GPC, opt-out, or not yet read) the result is `{ marketingAllowed: false }`
 * and no cookie or query parameter is read at all.
 *
 * `landingUrl` is the page URL without its query, except `source` — a query
 * string can carry anything, including personal data.
 */
export function readAdAttribution(
    doc: Pick<Document, 'cookie'>,
    loc: Pick<Location, 'origin' | 'pathname' | 'search'>,
    ga4MeasurementId: string | undefined,
    decision: boolean,
): AdAttribution {
    if (!decision) return { marketingAllowed: false };

    const out: AdAttribution = { marketingAllowed: true };
    const query = new URLSearchParams(loc.search || '');

    const gaClientId = parseGaClientId(readCookie(doc, '_ga'));
    if (gaClientId) out.gaClientId = gaClientId;

    const containerId = ga4MeasurementId?.trim().replace(/^G-/, '');
    if (containerId && /^[A-Za-z0-9]+$/.test(containerId)) {
        const sid = parseGaSessionId(readCookie(doc, `_ga_${containerId}`));
        if (sid) out.gaSessionId = sid;
    }

    const fbp = clean(readCookie(doc, '_fbp'));
    if (fbp) out.fbp = fbp;

    const fbclid = clean(query.get('fbclid') ?? undefined);
    const fbc = clean(readCookie(doc, '_fbc')) ?? (fbclid ? `fb.1.${Date.now()}.${fbclid}` : undefined);
    if (fbc && fbc.length <= AD_ATTRIBUTION_MAX_FIELD_LENGTH) out.fbc = fbc;

    const source = query.get('source');
    const landing = `${loc.origin}${loc.pathname}${source ? `?${new URLSearchParams({ source }).toString()}` : ''}`;
    const landingUrl = clean(landing);
    if (landingUrl) out.landingUrl = landingUrl;

    return out;
}

function toBase64Url(text: string): string {
    const bytes = new TextEncoder().encode(text);
    let bin = '';
    for (const b of bytes) bin += String.fromCharCode(b);
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(text: string): string {
    const b64 = text.replace(/-/g, '+').replace(/_/g, '/');
    const bin = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
}

/** base64url(JSON) of the allow-listed fields; drops `landingUrl` if over the size cap. */
export function encodeAttributionFragment(a: AdAttribution): string {
    const body: Record<string, unknown> = { marketingAllowed: a.marketingAllowed === true };
    if (body.marketingAllowed) {
        for (const key of STRING_FIELDS) {
            const v = a[key];
            if (typeof v === 'string' && v.length > 0 && v.length <= AD_ATTRIBUTION_MAX_FIELD_LENGTH) body[key] = v;
        }
    }
    let encoded = toBase64Url(JSON.stringify(body));
    if (encoded.length > AD_ATTRIBUTION_MAX_ENCODED_LENGTH) {
        delete body.landingUrl;
        encoded = toBase64Url(JSON.stringify(body));
    }
    return encoded;
}

/**
 * Parse an untrusted fragment value. Returns null for anything malformed:
 * oversize, not base64url JSON, not a plain object, an unknown key, a
 * non-string / over-long field, or a missing boolean `marketingAllowed`.
 */
export function decodeAttributionFragment(s: string): AdAttribution | null {
    if (typeof s !== 'string' || s.length === 0 || s.length > AD_ATTRIBUTION_MAX_ENCODED_LENGTH) return null;
    if (!/^[A-Za-z0-9_-]+$/.test(s)) return null;
    let parsed: unknown;
    try {
        parsed = JSON.parse(fromBase64Url(s));
    } catch {
        return null;
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
    const rec = parsed as Record<string, unknown>;
    for (const key of Object.keys(rec)) {
        if (!ALLOWED_KEYS.has(key)) return null;
    }
    if (typeof rec.marketingAllowed !== 'boolean') return null;
    const out: AdAttribution = { marketingAllowed: rec.marketingAllowed };
    for (const key of STRING_FIELDS) {
        const v = rec[key];
        if (v === undefined) continue;
        if (typeof v !== 'string' || v.length === 0 || v.length > AD_ATTRIBUTION_MAX_FIELD_LENGTH) return null;
        out[key] = v;
    }
    return out;
}
